'use client'
import { Cart } from '@/components/Cart'
import { OpenCartButton } from '@/components/Cart/OpenCart'
import { Link } from '@/i18n/routing'
import React, { Suspense, useState } from 'react'
import { SearchIcon, XIcon, UserIcon, LogOutIcon } from 'lucide-react'
import { Search } from '@/components/Search'
import { useAuth } from '@/providers/Auth'
import { LanguageToggle } from '@/components/LanguageToggle'
import { useTranslations } from 'next-intl'
import { AnnouncementBanner } from '@/components/AnnouncementBanner'
import { WishlistBadgeClient } from './WishlistBadgeClient'

import { MobileMenu } from './MobileMenu'
import type { Header } from 'src/payload-types'

import { HeaderNav } from './HeaderNav'
import { AdminUserStats } from './AdminUserStats'
import Image from 'next/image'
import { useLocale } from 'next-intl'

type Props = {
  header: Header
  initialWishlistCount?: number
}

export function HeaderClient({ header, initialWishlistCount = 0 }: Props) {
  const t = useTranslations('common')
  const navT = useTranslations('navigation')
  const locale = useLocale()
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const { user, logout } = useAuth()
  const menu = header.navItems || []

  const isAdmin = user?.roles && Array.isArray(user.roles) && user.roles.includes('admin')
  const showUserBar = user && !isAdmin

  return (
    <>
      <header className="sticky top-0 z-50 w-full flex flex-col">
        <div className="w-full bg-black/40 backdrop-blur-md border-b border-white/10 px-2 sm:px-4 lg:px-8 2xl:px-12">
          <div className="flex items-center justify-end gap-3 h-7 text-[11px] font-mono text-white/70">
            {/* Language group */}
            <div className="flex items-center flex-shrink-0">
              <LanguageToggle />
            </div>

            <div className="w-px h-3 bg-white/20 flex-shrink-0" aria-hidden="true" />

            {user ? (
              <>
                {/* Admin group */}
                {isAdmin && (
                  <div className="hidden md:flex items-center gap-3">
                    <a
                      href="/admin"
                      className="text-[var(--neon-emerald)] hover:text-white transition-colors tracking-widest uppercase flex items-center gap-1"
                    >
                      Dashboard
                    </a>
                    <div className="w-px h-3 bg-white/20 flex-shrink-0" aria-hidden="true" />
                    <AdminUserStats />
                    <div className="w-px h-3 bg-white/20 flex-shrink-0" aria-hidden="true" />
                  </div>
                )}

                {/* Account group */}
                <div className="flex items-center gap-3">
                  <span className="truncate max-w-[200px] hidden sm:inline">{user.email}</span>
                  <Link href="/account" className="hover:text-[var(--brand-red)] transition-colors">
                    {navT('account')}
                  </Link>
                  <button
                    onClick={() => logout()}
                    className="flex items-center gap-1 hover:text-[var(--brand-red)] transition-colors"
                  >
                    <LogOutIcon className="w-3 h-3" />
                    {navT('logout')}
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link href="/login" className="hover:text-[var(--brand-red)] transition-colors">
                  {navT('login', { fallback: 'Login' })}
                </Link>
                <span className="opacity-30">|</span>
                <Link
                  href="/create-account"
                  className="hover:text-[var(--brand-red)] transition-colors"
                >
                  {navT('register', { fallback: 'Register' })}
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="w-full border-b border-white/20 bg-white/5 dark:bg-black/20 backdrop-blur-xl shadow-lg relative">
          <nav className="px-2 sm:px-4 lg:px-8 2xl:px-12 flex items-center justify-between h-20">
            {/* 1. Left: Hamburger menu icon */}
            <div className="2xl:hidden flex-shrink-0 min-w-[40px] flex items-center">
              <Suspense fallback={null}>
                <MobileMenu menu={menu} />
              </Suspense>
            </div>

            {/* 2. Center: Logo image (Smoking Austria) */}
            <div className="flex-1 2xl:flex-none flex justify-center 2xl:justify-start items-center h-full overflow-hidden text-center 2xl:overflow-visible 2xl:mr-6">
              <Link
                href="/"
                className="relative flex justify-center 2xl:justify-start items-center h-full w-full max-w-[160px] sm:max-w-[200px] 2xl:max-w-none 2xl:w-48 2xl:-ml-6"
              >
                <Image
                  src="/smoking-logo.svg"
                  alt="Smoking Austria Logo"
                  width={400}
                  height={120}
                  className="h-16 sm:h-20 2xl:h-36 w-auto transition-all object-contain 2xl:absolute 2xl:left-0 2xl:top-1/2 2xl:-translate-y-1/2 2xl:max-w-none"
                  priority
                />
              </Link>
            </div>

            {/* Center: Navigation or Search */}
            <div className="hidden 2xl:flex absolute left-[52%] -translate-x-1/2 h-full items-center">
              {/* Navigation - hidden when search is open */}
              <div
                className={`transition-all duration-300 ease-out ${
                  isSearchOpen
                    ? 'opacity-0 blur-sm pointer-events-none absolute'
                    : 'opacity-100 blur-0'
                }`}
              >
                <HeaderNav header={header} />
              </div>

              {/* Search - shown when search is open */}
              <div
                className={`flex items-center gap-4 transition-all duration-300 ease-out ${
                  isSearchOpen
                    ? 'opacity-100 blur-0 w-[600px]'
                    : 'opacity-0 blur-sm pointer-events-none absolute w-0'
                }`}
              >
                <Search className="flex-1" onSelect={() => setIsSearchOpen(false)} />
                <button
                  onClick={() => setIsSearchOpen(false)}
                  className="p-2.5 hover:bg-white/10 rounded-full transition-all hover:rotate-90 duration-300 group flex-shrink-0"
                  aria-label={t('close')}
                >
                  <XIcon className="h-5 w-5 text-white group-hover:text-[var(--brand-red)]" />
                </button>
              </div>
            </div>

            {/* 3. Right: A group of icons */}
            <div className="flex-shrink-0 flex justify-end items-center gap-3 sm:gap-5 shrink-0">
              <WishlistBadgeClient locale={locale} initialCount={initialWishlistCount} />

              {/* Search toggle button */}
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="p-1.5 sm:p-2.5 hover:bg-white/10 rounded-full transition-all hover:scale-110 duration-200 group flex-shrink-0"
                aria-label={isSearchOpen ? t('close') : t('search')}
              >
                {isSearchOpen ? (
                  <>
                    <XIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white group-hover:text-[var(--brand-red)] 2xl:hidden" />
                    {/* Invisible icon on desktop to preserve width and prevent layout shift */}
                    <SearchIcon className="hidden 2xl:block h-5 w-5 opacity-0" />
                  </>
                ) : (
                  <SearchIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white group-hover:text-[var(--brand-red)]" />
                )}
              </button>

              <Suspense fallback={<OpenCartButton />}>
                <Cart />
              </Suspense>

              <Link
                href={user ? '/account' : '/login'}
                className="p-1.5 sm:p-2.5 border border-muted-foreground/30 rounded-full transition-all hover:scale-110 hover:border-[var(--brand-red)] duration-200 group flex-shrink-0"
                aria-label={navT('account')}
              >
                <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white group-hover:text-[var(--brand-red)]" />
              </Link>
            </div>
          </nav>
        </div>
      </header>
      <AnnouncementBanner announcement={header.announcement} />
    </>
  )
}
